import {
  Chat,
  ChatMessage,
  ChatParticipantDetail,
  ChatParticipantSummary,
  ChatRoomHistoryData,
  ChatState,
  ChatMessageApi,
} from "../types/chat";
import { CHAT_TEXT_CONSTANTS, ERROR_MESSAGES, PROFILE_ACTIONS } from "../utils/constants";
import { CHAT_ERROR_CONSTANTS } from "../utils/chatConstants";

export function getChatOtherUserDisplayName(chat: Chat): string {
  if (chat.otherUserIsDeactivated) {
    return CHAT_TEXT_CONSTANTS.DEACTIVATED_USER_NAME;
  }
  return chat.otherUserName || CHAT_TEXT_CONSTANTS.DEFAULT_CHAT_USER_NAME;
}

function parseParticipantSummary(raw: unknown): ChatParticipantSummary | null {
  if (!raw || typeof raw !== "object") {
    return null;
  }
  const obj = raw as Record<string, unknown>;
  const userId = typeof obj.userId === "string" ? obj.userId : "";
  if (!userId) {
    return null;
  }
  return {
    userId,
    userName: typeof obj.userName === "string" ? obj.userName : "",
    image:
      typeof obj.image === "string"
        ? obj.image
        : obj.image === null
          ? null
          : null,
    isDeactivated: Boolean(obj.isDeactivated),
  };
}

export function normalizeRoomHistoryPayload(
  raw: unknown
): ChatRoomHistoryData {
  if (Array.isArray(raw)) {
    return {
      chatMessages: raw as ChatMessageApi[],
      participantDetails: [],
      otherParticipantDetails: null,
    };
  }
  if (raw && typeof raw === "object") {
    const obj = raw as Record<string, unknown>;
    const participantDetails = Array.isArray(obj.participantDetails)
      ? (obj.participantDetails
        .map(parseParticipantSummary)
        .filter((participant): participant is ChatParticipantSummary => participant !== null) as ChatParticipantSummary[])
      : [];
    return {
      chatMessages: Array.isArray(obj.chatMessages)
        ? (obj.chatMessages as ChatMessageApi[])
        : [],
      participantDetails,
      otherParticipantDetails: parseParticipantSummary(obj.otherParticipantDetails),
    };
  }
  return { chatMessages: [], participantDetails: [], otherParticipantDetails: null };
}

function applyParticipantDetailToChat(
  chat: Chat,
  otherId: string,
  detail: ChatParticipantSummary
) {
  chat.otherUserId = otherId;
  chat.otherUserIsDeactivated = Boolean(detail.isDeactivated);
  chat.otherUserName = chat.otherUserIsDeactivated
    ? CHAT_TEXT_CONSTANTS.DEACTIVATED_USER_NAME
    : detail.userName;
  if (typeof detail.image === "string" && detail.image) {
    chat.otherUserAvatar = detail.image;
  } else {
    chat.otherUserAvatar = undefined;
  }
}

function resolveOtherParticipantDetail(
  otherId: string,
  participants: ChatParticipantDetail[],
  otherParticipantDetails: ChatParticipantSummary | null | undefined
): ChatParticipantSummary | undefined {
  if (
    otherParticipantDetails &&
    otherParticipantDetails.userId === otherId
  ) {
    return otherParticipantDetails;
  }
  return participants.find((p) => p.userId === otherId);
}

export function mergeReactionUserIntoReactions(
  reactions: Record<string, string[]>,
  reaction: string,
  userId: string
): Record<string, string[]> {
  const prevList = reactions[reaction] || [];
  if (prevList.includes(userId)) {
    return reactions;
  }
  return {
    ...reactions,
    [reaction]: [...prevList, userId],
  };
}

export function applyCreatedChatToState(state: ChatState, chat: Chat) {
  const existingChat = state.chats.find((c) => c.id === chat.id);
  if (!existingChat) {
    state.chats.unshift(chat);
  } else {
    const idx = state.chats.indexOf(existingChat);
    state.chats[idx] = { ...existingChat, ...chat };
  }
  state.currentChat = chat;
}

export function mergeUnreadFromPrevious(
  incoming: Chat[],
  previous: Chat[]
): Chat[] {
  const prevUnread = new Map(
    previous.map((chat) => [chat.id, chat.unreadCount ?? 0])
  );
  return incoming.map((chat) => ({
    ...chat,
    unreadCount: prevUnread.get(chat.id) ?? chat.unreadCount ?? 0,
  }));
}

export function applyParticipantDetailsFromHistory(
  state: ChatState,
  roomId: string,
  participants: ChatParticipantDetail[],
  currentUserId: string | null | undefined,
  otherParticipantDetails?: ChatParticipantSummary | null
) {
  if (!currentUserId) {
    return;
  }
  if (!participants.length && !otherParticipantDetails) {
    return;
  }
  const patchChat = (chat: Chat | null) => {
    if (!chat || chat.id !== roomId) {
      return;
    }
    const participantIds = Array.isArray(chat.participants)
      ? chat.participants
      : [];
    const otherIdFromParticipants = participants.find(
      (participant) => participant.userId !== currentUserId
    )?.userId;
    const otherId =
      chat.otherUserId ||
      otherParticipantDetails?.userId ||
      participantIds.find((id) => id !== currentUserId) ||
      otherIdFromParticipants;
    if (!otherId) {
      return;
    }
    const detail = resolveOtherParticipantDetail(
      otherId,
      participants,
      otherParticipantDetails
    );
    if (detail) {
      applyParticipantDetailToChat(chat, otherId, detail);
    }
  };
  state.chats.forEach((chat) => {
    if (chat.id === roomId) {
      patchChat(chat);
    }
  });
  patchChat(state.currentChat);
}

export function applyParticipantDetailsToChat(
  chat: Chat,
  participants: ChatParticipantDetail[],
  currentUserId: string,
  otherParticipantDetails?: ChatParticipantSummary | null
): Chat {
  if (!Array.isArray(participants) || participants.length === 0) {
    if (!otherParticipantDetails) {
      return chat;
    }
  }
  const participantIds = Array.isArray(chat.participants) ? chat.participants : [];
  const otherIdFromParticipants = participants.find(
    (participant) => participant.userId !== currentUserId
  )?.userId;
  const otherId =
    chat.otherUserId ||
    otherParticipantDetails?.userId ||
    participantIds.find((id) => id !== currentUserId) ||
    otherIdFromParticipants ||
    "";
  if (!otherId) {
    return chat;
  }
  const detail = resolveOtherParticipantDetail(
    otherId,
    participants,
    otherParticipantDetails
  );
  if (!detail) {
    return chat;
  }
  const updated: Chat = { ...chat, otherUserId: otherId };
  applyParticipantDetailToChat(updated, otherId, detail);
  return updated;
}

export function mergeHistoryWithRealtime(
  existing: ChatMessage[],
  roomId: string,
  fromApi: ChatMessage[]
): ChatMessage[] {
  const byId = new Map<string, ChatMessage>();
  for (const msg of fromApi) {
    byId.set(msg.messageId, msg);
  }
  for (const message of existing) {
    if (message.roomId !== roomId) {
      continue;
    }
    if (!byId.has(message.messageId)) {
      byId.set(message.messageId, message);
    }
  }
  return Array.from(byId.values()).sort((msg1, msg2) => msg1.timestamp - msg2.timestamp);
}

export function isDeactivatedUserSendError(error: unknown): boolean {
  const err = error as {
    response?: { status?: number; data?: { statusCode?: number; message?: string } };
    message?: string;
  };
  const status = err.response?.status ?? err.response?.data?.statusCode;
  const message = (err.response?.data?.message ?? err.message ?? "").toLowerCase();
  return status === 403 && message.includes(PROFILE_ACTIONS.DEACTIVATED);
}

export function isReportBlockSendError(error: unknown): boolean {
  const err = error as {
    response?: { data?: { message?: string } };
    message?: string;
  };
  const message = (err.response?.data?.message ?? err.message ?? "").toLowerCase();
  return message.includes(CHAT_ERROR_CONSTANTS.BLOCK_REPORT_RELATIONSHIP);
}

export function resolveSendMessageErrorDisplay(message: string): string {
  if (message.toLowerCase().includes(CHAT_ERROR_CONSTANTS.BLOCK_REPORT_RELATIONSHIP)) {
    return ERROR_MESSAGES.CANNOT_SEND_MESSAGE_REPORTED_USER;
  }
  return message;
}

export function markChatOtherUserDeactivated(state: ChatState, roomId: string) {
  const patch = (chat: Chat | null) => {
    if (chat && chat.id === roomId) {
      chat.otherUserIsDeactivated = true;
      chat.otherUserName = CHAT_TEXT_CONSTANTS.DEACTIVATED_USER_NAME;
      chat.otherUserAvatar = undefined;
    }
  };
  state.chats.forEach(patch);
  patch(state.currentChat);
}
